import { IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import Button from '../Button/Button'

/**
 *
 */
const AlertBox = ({
  title,
  titleWrap,
  message,
  onClose,
  onConfirm,
  confirmBtnText,
  confirmBtnClassName,
  cancelBtnText,
  cancelBtnClassName,
  confirmBtnType,
  cancelBtnType,
  confirmBtnDisabled,
  cancelBtnDisabled,
  loading,
  subMessage,
  usageCount,
  usageLabel = 'Tag Usage',
  popupClassName = '',
  viewType,
  anotherConfirmBtnText,
  anotherConfirmBtnType,
  onAnotherConfirm,
  options,
  selectedOption,
  onOptionChange,
  'data-testid': dataTestId,
}) => {
  const [deleteInput, setDeleteInput] = useState('')

  const isDeleteConfirmMode = subMessage === 'inputDeleteMessage'
  const isConfirmDisabled =
    confirmBtnDisabled || (isDeleteConfirmMode && deleteInput.trim() !== 'Delete')

  return createPortal(
    <div
      className="fixed top-[0px] left-0 w-full h-full z-[9999] bg-BG-Overlay-default"
      data-testid="alert-box"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-BG-Default-white shadow-md w-[42.5%] rounded-[8px] ${popupClassName}`}
        data-testid={dataTestId || 'alert-box-container'}
      >
        <div className="w-full p-4 pb-0">
          <div className="flex justify-between items-center">
            <div
              className={`text-base font-bold text-Text-large-08 ${titleWrap ? 'min-w-0 flex-1 pr-2 break-all' : ''}`}
              data-testid="alert-box-title"
            >
              {title}
            </div>
            <Button
              icon={<IconX className="w-5 h-5 text-Icon-strong-06" />}
              type="secondary-v2"
              className="p-2 active:bg-BG-Default-utility-gray-02"
              onClick={onClose}
              data-testid="alert-box-close-btn"
            />
          </div>
        </div>
        <div className="h-px bg-BG-Default-neutral-01 w-full my-2"></div>
        <div
          className="text-sm font-semibold text-Text-medium-06 px-4"
          data-testid="alert-box-message"
        >
          {message}
        </div>
        {options && options.length > 0 && (
          <div className="mt-4 px-4 flex flex-col gap-2" data-testid="alert-box-options">
            {options.map((option) => {
              const isSelected = selectedOption === option.id
              return (
                <div
                  key={option.id}
                  className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-Border-Selected-brand-06 bg-BG-Selected-brand-00'
                      : 'border-Border-Default-neutral-01 bg-BG-Default-white'
                  }`}
                  onClick={() => onOptionChange?.(option.id)}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  data-testid={`alert-box-option-${option.id}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onOptionChange?.(option.id)
                    }
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'border-Border-Selected-brand-06'
                          : 'border-Border-Default-neutral-01'
                      }`}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-Misc-brand" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-Text-large-08">{option.label}</div>
                      {option.description && (
                        <div className="mt-0.5 text-xs text-Text-small-04">
                          {option.description}
                        </div>
                      )}
                      {option.content && (
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                          {option.content}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {subMessage === 'archive' && (
          <div
            className="mx-4 mt-4 flex items-center justify-between rounded bg-BG-Default-neutral-00 px-3 py-2"
            data-testid="alert-box-usage-row"
          >
            <span className="text-sm text-Text-small-04">{usageLabel}:</span>
            <span
              className="text-sm font-semibold text-Text-medium-06"
              data-testid="alert-box-usage-count"
            >
              {usageCount ?? 0} {usageLabel === 'Tag Usage' ? 'Entities' : ''}
            </span>
          </div>
        )}
        {isDeleteConfirmMode && (
          <div className="mt-4 px-4" data-testid="alert-box-delete-confirm-section">
            <p className="text-sm text-Text-small-04 mb-2">
              To confirm, type &apos;Delete&apos; below
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="Enter"
              className="w-full rounded border border-Border-Default-neutral-01 bg-BG-Default-neutral-00 px-3 py-2 text-sm text-Text-medium-06 outline-none focus:border-Border-focus-brand"
              data-testid="alert-box-delete-confirm-input"
            />
          </div>
        )}
        {subMessage && subMessage !== 'archive' && subMessage !== 'inputDeleteMessage' && (
          <p className="mt-4 text-sm text-Text-small-04 px-4" data-testid="alert-box-submessage">
            {subMessage}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 p-4">
          {viewType === 'temp-add-new-shipment' && anotherConfirmBtnText ? (
            <Button
              text={anotherConfirmBtnText || 'Another Confirm'}
              type={anotherConfirmBtnType}
              className="w-fit py-1.5 px-4 text-sm font-medium"
              onClick={onAnotherConfirm}
              data-testid="alert-box-another-confirm-btn"
            />
          ) : (
            <Button
              text={cancelBtnText || 'Cancel'}
              type={cancelBtnType}
              className={`w-fit py-1.5 px-4 text-sm font-medium ${cancelBtnClassName}`}
              onClick={onClose}
              data-testid="alert-box-cancel-btn"
              disabled={cancelBtnDisabled}
            />
          )}
          <Button
            text={confirmBtnText || 'Confirm'}
            type={confirmBtnType}
            className={`w-fit py-1.5 px-4 text-sm font-medium ${confirmBtnClassName}`}
            onClick={onConfirm}
            data-testid="alert-box-confirm-btn"
            loading={loading}
            disabled={isConfirmDisabled}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default AlertBox
